import React, { useState } from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { Therapist } from './Sahayak';
import { startTavusConversation } from '@/services/tavusService';
import { AlertCircle } from 'lucide-react';

const MOCK_THERAPISTS: Therapist[] = [
	{
		id: "1",
		name: "Dr. Anubha Jain",
		specialization: ["Anxiety", "Depression", "CBT"],
		availability: ["Mon", "Wed", "Fri"],
		rating: 4.8,
		experience: 8,
		price: 100,
		image: "https://api.dicebear.com/7.x/avataaars/svg?seed=1"
	},
	{
		id: "2",
		name: "Dr. Siddharth Singh",
		specialization: ["Trauma", "PTSD", "DBT"],
		availability: ["Tue", "Thu", "Sat"],
		rating: 4.9,
		experience: 12,
		price: 120,
		image: "https://api.dicebear.com/7.x/avataaars/svg?seed=2"
	}
];

const TherapistListing: React.FC = () => {
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedSpecialization, setSelectedSpecialization] = useState<string>('');
	const [therapists, setTherapists] = useState<Therapist[]>(MOCK_THERAPISTS);
	const [loading, setLoading] = useState(false);
	const [tavusResponse, setTavusResponse] = useState<any>(null);
	const [tavusError, setTavusError] = useState<string | null>(null);

	const handleBooking = async () => {
		setLoading(true);
		setTavusError(null);
		setTavusResponse(null);
		try {
			const data = await startTavusConversation();
			setTavusResponse(data);
			console.log('Tavus API response:', data);
		} catch (error: any) {
			setTavusError(error.message || 'Failed to start conversation.');
			console.error('Tavus API error:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
		filterTherapists(event.target.value, selectedSpecialization);
	};

	const filterTherapists = (search: string, specialization: string) => {
		let filtered = MOCK_THERAPISTS;
		if (search) {
			filtered = filtered.filter(t => 
				t.name.toLowerCase().includes(search.toLowerCase()) ||
				t.specialization.some(s => s.toLowerCase().includes(search.toLowerCase()))
			);
		}
		if (specialization) {
			filtered = filtered.filter(t => 
				t.specialization.includes(specialization)
			);
		}
		setTherapists(filtered);
	};

	return (
		<div className="space-y-6">
			<div className="flex gap-4 mb-6">
				<Input
					placeholder="Search therapists..."
					value={searchTerm}
					onChange={handleSearch}
					className="max-w-sm bg-white/5 backdrop-blur-lg border border-white/10 text-white placeholder:text-white/60"
				/>
				<select
					className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-md p-2 text-white outline-none"
					value={selectedSpecialization}
					onChange={(e) => {
						setSelectedSpecialization(e.target.value);
						filterTherapists(searchTerm, e.target.value);
					}}
				>
					<option value="" className="bg-[#1A1F3C] text-white">All Specializations</option>
					<option value="Anxiety" className="bg-[#1A1F3C] text-white">Anxiety</option>
					<option value="Depression" className="bg-[#1A1F3C] text-white">Depression</option>
					<option value="Trauma" className="bg-[#1A1F3C] text-white">Trauma</option>
					<option value="CBT" className="bg-[#1A1F3C] text-white">CBT</option>
					<option value="DBT" className="bg-[#1A1F3C] text-white">DBT</option>
				</select>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
				{therapists.map((therapist) => (
					<Card key={therapist.id} className="bg-white/5 backdrop-blur-lg border border-white/10 p-4">
						<div className="flex items-start gap-4">
							<img
								src={therapist.image}
								alt={therapist.name}
								className="w-20 h-20 rounded-full border-2 border-[#8B5CF6]/30"
							/>
							<div className="w-full">
								<h3 className="font-semibold text-white">{therapist.name}</h3>
								<div className="flex flex-wrap gap-1 mt-2">
									{therapist.specialization.map((spec) => (
										<Badge key={spec} variant="secondary" className="bg-[#8B5CF6]/20 text-white border border-[#8B5CF6]/30">
											{spec}
										</Badge>
									))}
								</div>
								<p className="text-sm mt-2 text-white/80">
									Experience: {therapist.experience} years
								</p>
								<p className="text-sm text-white/80">
									Price: Rs. 350/hour
								</p>
								{!tavusResponse && (
									<Button
										className="mt-4 w-full bg-gradient-to-r from-[#8B5CF6] to-[#D946EF] text-white hover:opacity-90"
										onClick={handleBooking}
										disabled={loading}
									>
										{loading ? 'Starting Session...' : 'Book Session'}
									</Button>
								)}
								{tavusResponse && tavusResponse.conversation_url && (
									<div className="flex flex-col items-center justify-center mt-6">
										<a
											href={tavusResponse.conversation_url}
											target="_blank"
											rel="noopener noreferrer"
											className="inline-block px-8 py-4 bg-gradient-to-r from-[#8B5CF6] to-[#D946EF] text-white text-lg font-semibold rounded-lg shadow-lg hover:opacity-90 transition-all"
											style={{ margin: '0 auto' }}
										>
											Join Session
										</a>
									</div>
								)}
								{tavusError && (
									<Alert className="bg-white/5 border border-white/10 mt-2">
										<AlertCircle className="h-4 w-4 text-red-400" />
										<AlertDescription className="text-white">
											{tavusError}
										</AlertDescription>
									</Alert>
								)}
							</div>
						</div>
					</Card>
				))}
			</div>
		</div>
	);
};

export default TherapistListing;